"use client";

// OsMenu — the ONE menu, as a full PAGE-VIEW takeover, now a BREADCRUMB-STEERED
// TREE navigator (no longer a swipeable pager). Opening the ≡ fades the surface
// under a frosted veil; netti rises to centre stage (the host drives the organism
// above the veil); the navigator floats over it.
//
// Inside, a small breadcrumb — innernet › yours › projects — shows exactly where
// you are; each crumb walks you back up. Below it, the current directory's items
// are a big lowercase list. Interaction is TAP AND GO — one tap, no second tap:
//
//   • tap an item that has a page (company, docs, a project…) → COMMITS: the orb
//     turns ink, the menu collapses, and it opens for real. One tap. Reopen the
//     menu on that page to see the level below it.
//   • tap a pure directory (no page of its own, e.g. "founders") → drills IN one
//     level so you can reach the openable items inside it.
//
// Every click is narrated to the Noticer (innernet:clip note) where one is
// mounted. Two fixed layers so netti sits sharp BETWEEN them:
//   • .os-fullmenu-veil — the frosted scrim.
//   • .os-fullmenu      — the breadcrumb + list, above netti.

import { useCallback, useEffect, useRef, useState } from "react";
import MenuFooter from "../MenuFooter";
import CellAvatar from "../CellAvatar";
import { loadCard, type MeCard } from "../UserChip";
import type { MenuNode } from "../../lib/nav/siteNav";
import { lockScroll } from "../../lib/os/scrollLock";

export type { MenuNode } from "../../lib/nav/siteNav";

/** walk the tree along a path of keys → the node you're at + the full trail. */
function resolvePath(root: MenuNode, path: string[]): { node: MenuNode; trail: MenuNode[] } {
  const trail: MenuNode[] = [root];
  let node = root;
  for (const key of path.slice(1)) {
    const next = node.children?.find((c) => c.key === key);
    if (!next) break;
    node = next;
    trail.push(node);
  }
  return { node, trail };
}

/** narrate a click to the Noticer (StatusCorner listens for note flashes). */
function notice(text: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: text } }));
}

export default function OsMenu({
  open,
  root,
  startPath = ["innernet"],
  onClose,
  veil = true,
}: {
  open: boolean;
  /** the breadcrumb tree, rooted at "innernet" (from buildMenuTree). */
  root: MenuNode;
  /** where the breadcrumb opens (root, or drilled into a space). */
  startPath?: string[];
  onClose: () => void;
  /** render OsMenu's own frosted veil. The landing opts out (`false`). */
  veil?: boolean;
}) {
  // the person's own face for the "yours" row — the same cached card the header
  // chip reads, asked for only once the menu actually opens
  const [me, setMe] = useState<MeCard | null>(null);
  useEffect(() => {
    if (!open || me) return;
    let live = true;
    loadCard().then((c) => { if (live && c) setMe(c); });
    return () => { live = false; };
  }, [open, me]);

  const [path, setPath] = useState<string[]>(startPath);
  // the lit item (tap feedback) and the committing item (orb → ink on open).
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [committing, setCommitting] = useState<string | null>(null);
  const dirRef = useRef(1); // +1 drilling deeper, -1 stepping back — drives the box slide.

  const startRef = useRef(startPath);
  startRef.current = startPath;
  // The shell hands `onClose` as an inline arrow, new on EVERY shell render. Held
  // in a ref, a render that happens while the menu is open (a live pulse, a poll)
  // no longer re-runs the open effect below — which used to snap you back to the
  // root and kill a tap's 60ms commit timer mid-flight.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimers = () => {
    if (commitTimer.current) { clearTimeout(commitTimer.current); commitTimer.current = null; }
  };

  // the page holds still while the menu is up (the counted lock: a sheet
  // closing under it never hands the page back, nor the other way round)
  useEffect(() => (open ? lockScroll("menu") : undefined), [open]);

  // Escape closes; reset to the start path each open.
  useEffect(() => {
    if (!open) return;
    setPath(startRef.current);
    setActiveKey(null);
    setCommitting(null);
    dirRef.current = 1;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCloseRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { node: currentNode, trail } = resolvePath(root, path);
  const items = currentNode.children ?? [];

  // step the breadcrumb: down (drill) or up (a crumb click). Records direction so
  // the incoming box slides the right way.
  const setDepth = useCallback((next: string[], dir: number) => {
    dirRef.current = dir;
    setActiveKey(null);
    clearTimers();
    setPath(next);
  }, []);

  const drill = (n: MenuNode) => setDepth([...path, n.key], 1);
  const toCrumb = (idx: number) => { if (idx < path.length - 1) setDepth(path.slice(0, idx + 1), -1); };

  // commit — the orb flips to ink, the menu collapses, the target opens.
  const commit = (n: MenuNode) => {
    clearTimers();
    setCommitting(n.key);
    setActiveKey(n.key);
    notice(`entering ${n.label}`);
    commitTimer.current = setTimeout(() => {
      if (n.onOpen) n.onOpen();
      // committing ALWAYS collapses the menu. Most onOpen handlers close on
      // their own (buildMenuTree's go()), but a surface's own section items
      // (MenuSpace.onSelect via itemToNode) never did — clicking one routed
      // in the background while the takeover stayed up (John, 2026-08-31).
      onCloseRef.current();
      // law_snappy — one frame of ink on the orb is the acknowledgement; the
      // target opens under the collapsing menu rather than after it (was 240ms,
      // which read as the click not having landed).
    }, 60);
  };

  // tap and go — one tap, no second tap. A node that has a page (onOpen) commits
  // straight to it; a pure directory (no page of its own, e.g. "founders") drills
  // one level in so its openable items are reachable.
  const onRow = (n: MenuNode) => {
    if (committing) return;
    setActiveKey(n.key); // instant: opacity lifts + the orb lights
    if (n.onOpen) commit(n);
    else if (n.children?.length) drill(n);
  };

  return (
    <>
      {/* the frosted scrim. The landing opts out and paints its own veil. */}
      {veil && <div className="os-fullmenu-veil" data-open={open || undefined} aria-hidden />}

      <div
        className="os-fullmenu"
        data-open={open || undefined}
        aria-hidden={!open}
        onClick={(e) => {
          if (!(e.target as HTMLElement).closest(".os-fullmenu__row, .os-fullmenu__crumb, .menu-foot")) onClose();
        }}
      >
        {/* the navigator breadcrumb — where you are, and the way back up. */}
        {/* THE CRUMB EARNS ITS PLACE. At the ROOT there is nothing to walk back
            to and the wordmark is already on screen a few lines above — a lone
            "innernet" here just said the same word twice. It appears the moment
            you go a level deeper, where it is the way back. */}
        <nav className="os-fullmenu__crumb" aria-label="location" data-hidden={trail.length < 2 || undefined}>
          {trail.map((n, i) => {
            const isCur = i === trail.length - 1;
            return (
              <span key={n.key} className="os-fullmenu__crumbseg">
                {i > 0 && <span className="os-fullmenu__chev" aria-hidden>›</span>}
                <button
                  type="button"
                  className="os-fullmenu__seg"
                  data-cur={isCur || undefined}
                  tabIndex={open ? 0 : -1}
                  onClick={() => toCrumb(i)}
                >
                  {n.label}
                </button>
              </span>
            );
          })}
        </nav>

        {/* the current directory's items — re-keyed on the path so the box slides
            + the rows re-stagger each time you drill or step back. */}
        <div
          key={path.join("/")}
          className="os-fullmenu__scroll"
          data-dir={dirRef.current >= 0 ? "fwd" : "back"}
        >
          <div className="os-fullmenu__list" data-count={items.length}>
            {items.map((n, i) => {
              const isDir = !!n.children?.length;
              return (
                <button
                  key={n.key}
                  type="button"
                  className="os-fullmenu__row"
                  data-here={n.here || undefined}
                  data-active={activeKey === n.key || undefined}
                  data-committing={committing === n.key || undefined}
                  data-dir={isDir || undefined}
                  data-hint={`${n.label} — ${n.onOpen ? "open" : "browse"}`}
                  style={{ ["--i" as string]: String(i) } as React.CSSProperties}
                  tabIndex={open ? 0 : -1}
                  onClick={() => onRow(n)}
                >
                  <span className="os-fullmenu__name">
                    {n.avatar && me && (
                      <CellAvatar name={me.name} src={me.avatarUrl} size={30} fontSize="0.8rem" className="os-fullmenu__face" />
                    )}
                    {/* "enter" — the same cell, empty: the place is waiting for you */}
                    {n.cell && <CellAvatar name="" size={30} className="os-fullmenu__face" />}
                    {n.label}
                    <span className="os-fullmenu__orb" aria-hidden />
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* a progressive-blur band across the bottom — the list dissolves INTO a
            soft frost as it nears the footer, so the end of the section reads as
            a gentle horizon rather than a hard cut. Sits above the rows, below the
            footer text; pointer-transparent so scrolling still works through it. */}
        <div className="os-fullmenu__endfade" data-open={open || undefined} aria-hidden />
      </div>

      {/* the FULL footer — mark · public links · PH · social — on every surface. */}
      <MenuFooter open={open} />
    </>
  );
}
